import { parseGeoData, pipe } from "../utils/helpers";

export function search(dbData, userInput, dbDataKeys) {
  const sanitizedUserInput = userInput.toString().toLowerCase();
  if (!userInput) return dbData;

  return dbData.filter((object) =>
    dbDataKeys.some(
      (dbDataKey) =>
        object[dbDataKey] &&
        object[dbDataKey].toString().toLowerCase().includes(sanitizedUserInput)
    )
  );
}

export function sort(items, dropdown) {
  switch (dropdown) {
    case "newest":
      return [...items].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
    case "hottest":
      return [...items].sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0));
    case "aToZ":
      return [...items].sort((a, b) => {
        const titleA = a.title ? a.title.toLowerCase() : "";
        const titleB = b.title ? b.title.toLowerCase() : "";
        return titleA.localeCompare(titleB);
      });
    case "zToA":
      return [...items].sort((a, b) => {
        const titleB = b.title ? b.title.toLowerCase() : "";
        const titleA = a.title ? a.title.toLowerCase() : "";
        return titleB.localeCompare(titleA);
      });
    case "localityName":
      return [...items].sort((a, b) => {
        const localityA = a.localityName ? a.localityName.toLowerCase() : "";
        const localityB = b.localityName ? b.localityName.toLowerCase() : "";
        return localityA.localeCompare(localityB);
      });
    default:
      return items;
  }
}

// export function filterByTopic(items, selectedTopics) {
//   return selectedTopics.length > 0
//     ? items?.filter(({ topic }) => selectedTopics.includes(topic))
//     : items;
// }

function filterByDemographics(item, filter) {
  // If no filters selected, return true
  if (filter.selectedFilters.length === 0) {
    return true;
  }


  // Get the value either from item directly or from userData
  const value = item[filter.filterName] || 
                (item?.userData && item.userData[filter.filterName]);

  // Check if the value exists and is included in selectedFilters
  return value != null && filter.selectedFilters.includes(value);
}

function filterByGeoOptions(item, filter) {
  // Store original geoData if not already stored
  if (!item._originalGeoData) {
    item._originalGeoData = {};
    for (const key in item.values) {
      if (Object.prototype.hasOwnProperty.call(item.values, key) && item.values[key]?.geoData) {
        item._originalGeoData[key] = item.values[key].geoData;
      }
    }
  }

  // If no filters selected, restore original geoData
  if (filter.selectedFilters.length === 0) {
    for (const key in item.values) {
      if (Object.prototype.hasOwnProperty.call(item.values, key) && item._originalGeoData?.[key]) {
        item.values[key].geoData = item._originalGeoData[key];
      }
    }
    return true;
  }

  let foundMatch = false;
  for (const key in item.values) {
    if (Object.prototype.hasOwnProperty.call(item.values, key) && item.values[key]?.geoData) {
      const geoData = parseGeoData(item._originalGeoData[key]);

      if (geoData && Array.isArray(geoData.features)) {
        const filteredFeatures = geoData.features.filter(feature => {
          const choices = feature.properties?.choices;
          if (filter.selectedFilters.includes("other")) {
            if (filter.selectedFilters.length === 1) {
              return choices === "other";
            } else {
              return choices === "other" ||
                (choices && filter.selectedFilters.filter(f => f !== "other").includes(choices));
            }
          }
          return choices && filter.selectedFilters.includes(choices);
        });

        if (filteredFeatures.length > 0) {
          foundMatch = true;
          const filteredGeoData = {
            type: "FeatureCollection",
            features: filteredFeatures
          };
          item.values[key].geoData = JSON.stringify(filteredGeoData);
        } else {
          item.values[key].geoData = JSON.stringify({
            type: "FeatureCollection",
            features: []
          });
        }
      }
    }
  }
  return foundMatch;
}

function filterByTags(item, filter) {
  // Skip if it's a geoOptions or demographic filter
  // if (filter.filterName === "geoOptions" ||
  //   ["gender", "ageGroup", "userPostcode", "lang", "topic"].includes(filter.filterName)) {
  //   return undefined;
  // }

  // Store original geoData if not already stored
  if (!item._originalGeoData) {
    item._originalGeoData = {};
    for (const key in item.values) {
      if (Object.prototype.hasOwnProperty.call(item.values, key) && item.values[key]?.geoData) {
        item._originalGeoData[key] = item.values[key].geoData;
      }
    }
  }

  // If no filters selected, restore original geoData and return true
  if (filter.selectedFilters.length === 0) {
    if (item._originalGeoData) {
      for (const key in item.values) {
        if (Object.prototype.hasOwnProperty.call(item.values, key) && item._originalGeoData[key]) {
          item.values[key].geoData = item._originalGeoData[key];
        }
      }
    }
    return true;
  }

  // Special handling for topic filter
  if (filter.filterName === "topic") {
    return filter.selectedFilters.includes(item.topic);
  }

  // First check item-level tags (for regular text data)
  if (Array.isArray(item.tags)) {
    const matchingTag = item.tags.some(
      (tag) =>
        tag.key === filter.filterName &&
        filter.selectedFilters.includes(tag.value)
    );
    if (matchingTag) {
      return true;
    }
  }

  // Then check for geodata tags
  let foundMatch = false;
  if (item.values) {
    for (const key in item.values) {
      if (Object.prototype.hasOwnProperty.call(item.values, key)) {
        const valueObj = item.values[key];

        // Check geodata tags
        if (valueObj?.geoData) {
          const geoData = parseGeoData(item._originalGeoData[key]);
          if (geoData?.features) {
            const filteredFeatures = geoData.features.filter(feature =>
              feature.properties?.tags?.some(
                tag =>
                  tag.key === filter.filterName &&
                  filter.selectedFilters.includes(tag.value)
              )
            );

            if (filteredFeatures.length > 0) {
              foundMatch = true;
              const filteredGeoData = {
                type: "FeatureCollection",
                features: filteredFeatures
              };
              item.values[key].geoData = JSON.stringify(filteredGeoData);
            }
          }
        }

        // Check regular tags in values
        if (Array.isArray(valueObj.tags)) {
          const matchingTag = valueObj.tags.some(
            (tag) =>
              tag.key === filter.filterName &&
              filter.selectedFilters.includes(tag.value)
          );
          if (matchingTag) {
            foundMatch = true;
          }
        }
      }
    }
  }

  // If no matches found, set empty features for all geoData
  if (!foundMatch && item.values) {
    for (const key in item.values) {
      if (item.values[key]?.geoData) {
        item.values[key].geoData = JSON.stringify({
          type: "FeatureCollection",
          features: []
        });
      }
    }
  }

  return foundMatch;
}



export function filterByArealTagFilter(items, selectedArealFilter, mapBounds) {
  if (selectedArealFilter === "location") {
    return items?.filter(({ geoDataType }) => geoDataType === "location");
  }

  // console.log("noLocationFilter", selectedArealFilter, items);

  if (selectedArealFilter === "noLocation") {
    // console.log("noLocationFilter", items);
    return items?.filter(({ geoDataType }) => geoDataType === "noLocation");
  }
  if (selectedArealFilter === "path") {
    return items?.filter(({ geoDataType }) => geoDataType === "path");
  }
  if (selectedArealFilter === "area") {
    return items?.filter(({ geoDataType }) => geoDataType === "area");
  }

  if (selectedArealFilter === "inMapArea") {
    filterByMapbounds(items, mapBounds);
  }
  return items;
}

export function filterByStatus(items, statuses) {
  if (statuses.length === 0) {
    return items;
  }
  return items.filter(({ status }) => statuses.includes(status));
}

export function countStatusOfIdeas(ideas) {
  const statuses = {
    Unprocessed: 0,
    Accepted: 0,
    Planning: 0,
    Implemented: 0,
    Rejected: 0,
  };

  for (const iterator of Object.keys(statuses)) {
    const numOfIdeas = ideas.filter(({ status }) => status === iterator).length;
    statuses[iterator] = numOfIdeas;
  }

  return statuses;
}

export function filterByMapbounds(items, mapBounds) {
  if (!mapBounds) return items;
  return items?.filter(
    ({ lat, long }) =>
      lat <= mapBounds?.latitude1 &&
      lat >= mapBounds?.latitude2 &&
      long >= mapBounds?.longitude2 &&
      long <= mapBounds?.longitude3
  );
}

function filterByTopic(item, filter) {
  if (filter.filterName !== "topic") {
    return undefined;
  }

  return filter.selectedFilters.length === 0 || filter.selectedFilters.includes(item.topic);
}

function filterByCrossFilters(item, crossFilters) {
  // If no cross filters, return true
  if (!crossFilters || crossFilters.length === 0) {
    return true;
  }

  // Check if the item has values that match all cross filters
  return crossFilters.every(filter => {
    const questionId = filter.filterName.replace('crossFilter_', '');
    const selectedAnswers = filter.selectedFilters;

    // If no answers selected for this question, skip it
    if (!selectedAnswers || selectedAnswers.length === 0) {
      return true;
    }
    console.log(crossFilters);
    // Also check in values object
    if (item?.values?.[questionId]) {
        const page = item.values[questionId];

        if(page?.value && typeof page?.value === "string") {

          console.log("AAAAAA",page?.value);
          if (selectedAnswers.includes(page?.value)) {
            return true;
          }
        } else if (page?.value && Array.isArray(page?.value)) {
          if (page?.value?.some(val => selectedAnswers.includes(val))) {
            return true;
          }
        }
    }

    console.log('No match found for question:', questionId);
    return false;
  });
}

export const filterDbData = (
  type,
  data,
  searchTerm,
  keysInDB,
  activeFilters,
  dropdown,
  mapBounds,
  selectedArealFilter,
  project
) => {
  const demographicFilterKeys = [
    "gender",
    "ageGroup",
    "lang",
  ];

  if (!project?.settings?.demographicData?.withoutPostcode) {
    demographicFilterKeys.push("userPostcode");
  }

  if (project?.settings?.demographicData?.fields) {
    demographicFilterKeys.push(...project.settings.demographicData.fields.map(field => field.name));
  }

  const filter = pipe(
    (data) => search(data, searchTerm, keysInDB),
    (data) => {
      // Get cross filters
      const crossFilters = activeFilters.filter(filter => 
        filter.filterName.startsWith('crossFilter_')
      );

      return crossFilters.length === 0
        ? data
        : data.filter(item => filterByCrossFilters(item, crossFilters));
    },
    (data) => {
      // Simplified demographic filtering
      const demographicFilters = activeFilters.filter(filter =>
        demographicFilterKeys.includes(filter.filterName)
      );

      return demographicFilters.length === 0 
        ? data 
        : data.filter(item => demographicFilters.every(filter => filterByDemographics(item, filter)));
    },
    (data) => {
      // Get topic filters
      const topicFilter = activeFilters.find(filter => filter.filterName === "topic");
      if (topicFilter) {
        return data.filter(item => filterByTopic(item, topicFilter));
      }
      return data;
    },
    (data) => {
      // Get geoOptions filters
      const geoOptionsFilter = activeFilters.find(filter => filter.filterName === "geoOptions");

      if (!geoOptionsFilter) {
        return data.map(item => {
          if (item._originalGeoData) {
            for (const key in item.values) {
              if (Object.prototype.hasOwnProperty.call(item.values, key) && item._originalGeoData[key]) {
                item.values[key].geoData = item._originalGeoData[key];
              }
            }
          }
          return item;
        });
      }

      return data.filter(item => filterByGeoOptions(item, geoOptionsFilter));
    },
    (data) => {
      const tagFilters = activeFilters.filter(filter =>
        ![...demographicFilterKeys, "topic", "geoOptions"].includes(filter.filterName) &&
        !filter.filterName.startsWith('crossFilter_')
      );

      return data.filter(item =>
        tagFilters.every(filter => filterByTags(item, filter) !== false)
      );
    },
    (data) => filterByArealTagFilter(data, selectedArealFilter, mapBounds),
    (data) => sort(data, dropdown),
    (data) => filterByMapbounds(data, mapBounds)
  );

  return filter(data);
};
